/**
 * TierPicker — simplified replacement for the full AI Toolkit page.
 *
 * The user picks a single quality tier (Economy / Standard / Premium) and the
 * component builds the `selectedModels` map for every capability:
 *
 *   • Capability has an explicit `tiers[<tierId>][0]`   → use it.
 *   • Capability has no tier mapping for this tier      → use the
 *     highest-priority model from `capability.models` (by `priority`/`rank`
 *     field, else catalog order). Next priority = runtime fallback.
 *
 * The resulting map is persisted to `localStorage.selectedAIModels` plus
 * `selectedAITier` (so downstream fallback caps the chain at-or-below the
 * user's chosen tier) + `aiCapabilityTiers` (so the model-cascade resolver
 * can walk the full tier catalog on failure). Same keys the old
 * AIToolkitSelector wrote, so no downstream code needs to change.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, ArrowRight, Check, Loader2, Rocket, Zap, Gem, Info, Coins } from 'lucide-react';
import { fetchAvailableModels } from '../../services/api';
import { buildSelectedModelsForTier, previewTierSelection, TIER_IDS } from './tierModelResolver';
import { estimateTierCost, parseDurationSeconds, formatCredits } from './tierCostEstimator';

const TIER_CARDS = [
    {
        id: 'economy',
        name: 'Economy',
        tagline: 'Lowest cost · Faster turnaround',
        description: 'Cheapest available models in every capability. Best for drafts, test shots, and quick iterations where you want to move fast.',
        icon: Rocket,
        ring: 'ring-emerald-400/60',
        border: 'border-emerald-500/40',
        bg: 'bg-emerald-500/10',
        bgSoft: 'bg-emerald-500/5',
        text: 'text-emerald-300',
        chip: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30',
    },
    {
        id: 'standard',
        name: 'Standard',
        tagline: 'Balanced quality & cost',
        description: 'Mid-range models tuned for daily production work. A great balance of quality, speed and credit spend.',
        icon: Zap,
        ring: 'ring-violet-400/60',
        border: 'border-violet-500/40',
        bg: 'bg-violet-500/10',
        bgSoft: 'bg-violet-500/5',
        text: 'text-violet-300',
        chip: 'bg-violet-500/15 text-violet-200 border-violet-500/30',
    },
    {
        id: 'premium',
        name: 'Premium',
        tagline: 'Best-in-class output',
        description: 'Highest-fidelity models in every capability. Use this when polish, realism and detail matter most.',
        icon: Gem,
        ring: 'ring-amber-400/60',
        border: 'border-amber-500/40',
        bg: 'bg-amber-500/10',
        bgSoft: 'bg-amber-500/5',
        text: 'text-amber-300',
        chip: 'bg-amber-500/15 text-amber-200 border-amber-500/30',
    },
];

export default function TierPicker({ onContinue, onBack, duration }) {
    const [capabilities, setCapabilities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTier, setSelectedTier] = useState(() => {
        try {
            const stored = localStorage.getItem('selectedAITier');
            if (stored && TIER_IDS.includes(stored)) return stored;
        } catch { /* noop */ }
        return 'standard';
    });
    const [showPreview, setShowPreview] = useState(false);

    // Parse the project's chosen duration (e.g. "60 seconds") into seconds so
    // we can price every tier. If no duration is supplied yet (edge case —
    // the user bypassed the Setup step) we hide the cost chip rather than
    // showing a misleading zero.
    const durationSeconds = useMemo(() => parseDurationSeconds(duration), [duration]);
    const costByTier = useMemo(() => {
        if (!durationSeconds) return null;
        return {
            economy:  estimateTierCost(durationSeconds, 'economy'),
            standard: estimateTierCost(durationSeconds, 'standard'),
            premium:  estimateTierCost(durationSeconds, 'premium'),
        };
    }, [durationSeconds]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true); setError(null);
                const caps = await fetchAvailableModels();
                if (!cancelled) setCapabilities(caps || []);
            } catch (e) {
                if (!cancelled) setError(e?.message || 'Failed to load the model catalog. Please try again.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const preview = useMemo(
        () => previewTierSelection(capabilities, selectedTier),
        [capabilities, selectedTier]
    );

    const handleContinue = () => {
        if (loading || !capabilities.length) return;
        const { selectedModels } = buildSelectedModelsForTier(capabilities, selectedTier);
        if (onContinue) onContinue(selectedModels);
    };

    return (
        <div className="h-full overflow-y-auto" data-testid="tier-picker-scroll">
            <div className="max-w-6xl mx-auto py-8 px-4">
                <div className="bg-zinc-950/80 border border-white/10 rounded-2xl p-6 sm:p-8 shadow-2xl">
                    {/* Heading */}
                    <div className="flex items-start gap-3 mb-6">
                        <div className="w-12 h-12 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
                            <Zap className="w-6 h-6 text-violet-300" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Pick a quality tier</h2>
                            <p className="text-sm text-zinc-400 mt-1">
                                We'll pick the best AI models for every step of your video automatically. You can change this later from the project settings.
                            </p>
                            {durationSeconds && (
                                <p className="text-xs text-zinc-500 mt-2" data-testid="tier-picker-duration-hint">
                                    Showing <span className="text-zinc-300 font-semibold">approximate</span> credit cost for a <span className="text-zinc-300 font-semibold">{durationSeconds}-second</span> video. Actual spend may vary slightly depending on your story and script complexity.
                                </p>
                            )}
                        </div>
                    </div>

                    {error && (
                        <div className="mb-4 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-xs" data-testid="tier-picker-error">
                            {error}
                        </div>
                    )}

                    {loading ? (
                        <div className="flex items-center gap-2 text-zinc-400 py-16 justify-center" data-testid="tier-picker-loading">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-sm">Loading available models…</span>
                        </div>
                    ) : (
                        <>
                            {/* Tier cards */}
                            <div className="grid sm:grid-cols-3 gap-3 mb-6" data-testid="tier-picker-cards">
                                {TIER_CARDS.map(tier => {
                                    const Icon = tier.icon;
                                    const active = selectedTier === tier.id;
                                    const cost = costByTier?.[tier.id];
                                    return (
                                        <button
                                            key={tier.id}
                                            type="button"
                                            onClick={() => setSelectedTier(tier.id)}
                                            className={`text-left p-5 rounded-xl border transition-all ${
                                                active
                                                    ? `${tier.border} ${tier.bg} ring-2 ${tier.ring}`
                                                    : `border-white/10 ${tier.bgSoft} hover:border-white/20`
                                            }`}
                                            data-testid={`tier-card-${tier.id}`}
                                            aria-pressed={active}
                                        >
                                            <div className="flex items-start justify-between mb-3">
                                                <div className={`w-10 h-10 rounded-lg ${tier.bg} border ${tier.border} flex items-center justify-center`}>
                                                    <Icon className={`w-5 h-5 ${tier.text}`} />
                                                </div>
                                                {active && (
                                                    <div className={`w-6 h-6 rounded-full ${tier.bg} border ${tier.border} flex items-center justify-center`} data-testid={`tier-card-${tier.id}-check`}>
                                                        <Check className={`w-3.5 h-3.5 ${tier.text}`} />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="text-lg font-bold text-white mb-0.5">{tier.name}</div>
                                            <div className={`text-xs font-medium ${tier.text} mb-2`}>{tier.tagline}</div>
                                            <div className="text-xs text-zinc-400 leading-relaxed mb-3">{tier.description}</div>
                                            {Number.isFinite(cost) && (
                                                <div
                                                    className={`flex items-baseline gap-1.5 pt-3 border-t border-white/5`}
                                                    data-testid={`tier-card-${tier.id}-cost`}
                                                >
                                                    <Coins className={`w-3.5 h-3.5 ${tier.text}`} />
                                                    <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">≈</span>
                                                    <span className={`text-lg font-bold ${tier.text}`} data-testid={`tier-card-${tier.id}-cost-amount`}>{formatCredits(cost)}</span>
                                                    <span className="text-[11px] text-zinc-500 font-medium">credits</span>
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Preview toggle */}
                            {preview.length > 0 && (
                                <div className="mb-6">
                                    <button
                                        type="button"
                                        onClick={() => setShowPreview(v => !v)}
                                        className="flex items-center gap-2 text-xs text-zinc-400 hover:text-zinc-200 transition-colors"
                                        data-testid="tier-preview-toggle"
                                    >
                                        <Info className="w-3.5 h-3.5" />
                                        {showPreview ? 'Hide' : 'Show'} the {preview.length} models that will be used for the {TIER_CARDS.find(t => t.id === selectedTier)?.name} tier
                                    </button>
                                    {showPreview && (
                                        <div className="mt-3 rounded-xl border border-white/10 bg-black/30 divide-y divide-white/5" data-testid="tier-preview-list">
                                            {preview.map((row, idx) => (
                                                <div key={`${row.capabilityKey}-${idx}`} className="flex items-center justify-between gap-3 px-3 py-2 text-xs">
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-zinc-300 truncate" data-testid={`tier-preview-cap-${row.capabilityKey}`}>{row.capability}</div>
                                                        <div className="text-zinc-500 text-[11px] truncate">{row.model}</div>
                                                    </div>
                                                    <span className={`shrink-0 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${
                                                        row.tiered
                                                            ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                                                            : 'bg-zinc-700/40 text-zinc-400 border border-zinc-700/50'
                                                    }`} title={row.tiered ? 'Chosen from this tier.' : 'Capability has no models for this tier — using the highest-priority model as a safe default.'}>
                                                        {row.tiered ? 'Tier' : 'Priority'}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Nav buttons */}
                            <div className="flex items-center justify-between gap-3 pt-4 border-t border-white/10">
                                <button
                                    type="button"
                                    onClick={onBack}
                                    className="px-4 py-2 rounded-lg border border-white/15 text-sm text-zinc-300 hover:bg-white/5 transition-all flex items-center gap-2"
                                    data-testid="tier-picker-back-btn"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    Back
                                </button>
                                <button
                                    type="button"
                                    onClick={handleContinue}
                                    disabled={loading || !capabilities.length}
                                    className="px-5 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-all flex items-center gap-2 shadow-lg shadow-violet-600/30"
                                    data-testid="tier-picker-continue-btn"
                                >
                                    Continue
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
